import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { fetchLdVariants } from '@/api/catBrand';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { clearOtherPrevBrandAnswerAnswers } from '@/lib/otherPrevBrandAnswerStorage';
import { clearOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import {
  LD_MOTIVATION_OPTION_KEYS,
  getSelectedLdMotivations,
  readLdProductNowAnswers,
  writeLdProductNowAnswers,
  type LdMotivationKey,
  type LdProductNowAnswers,
} from '@/lib/ldProductNowStorage';
import {
  readPrevBrandAnswerAnswers,
  writePrevBrandAnswerAnswers,
} from '@/lib/prevBrandAnswerStorage';
import { clearSetBLdBrandOpinionAnswers } from '@/lib/setBLdBrandOpinionStorage';
import { writePrevBrandAnswers } from '@/lib/prevBrandStorage';
import { surveyChoiceInputClass } from '@/lib/surveyChoiceInput';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const labelClass = 'text-left text-[15px] font-medium leading-tight text-black sm:text-[16px]';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

export default function LdProductNow() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [variants, setVariants] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);
  const [answers, setAnswers] = useState<LdProductNowAnswers>(() => readLdProductNowAnswers());

  useEffect(() => {
    writeLdProductNowAnswers(answers);
  }, [answers]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setWarning(null);
    void (async () => {
      try {
        const list = await fetchLdVariants();
        if (cancelled) return;
        setVariants(list);
      } catch {
        if (!cancelled) setWarning(t.ldProductNow.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t.ldProductNow.loadError]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  if (questionnaireSet === 'A') return <Navigate to="/survey/prevbrand" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet !== 'B') return <Navigate to="/survey" replace />;

  const toggleMotivation = (key: Exclude<LdMotivationKey, 'other'>) => {
    setAnswers((prev) => ({
      ...prev,
      motivations: {
        ...prev.motivations,
        [key]: !prev.motivations[key],
      },
    }));
    setWarning(null);
  };

  const toggleOther = () => {
    setAnswers((prev) => ({
      ...prev,
      motivations: {
        ...prev.motivations,
        other: !prev.motivations.other,
      },
    }));
    setWarning(null);
  };

  const handleContinue = () => {
    const variant = answers.variant.trim();
    if (!variant) {
      setWarning(t.ldProductNow.variantWarning);
      return;
    }

    const selected = getSelectedLdMotivations(answers);
    if (selected.length === 0) {
      setWarning(t.ldProductNow.motivationWarning);
      return;
    }
    if (answers.motivations.other && !answers.motivations.otherSpecify.trim()) {
      setWarning(t.ldProductNow.otherMotivationWarning);
      return;
    }

    if (answers.smokedOtherProduct !== 'yes' && answers.smokedOtherProduct !== 'no') {
      setWarning(t.ldProductNow.otherProductWarning);
      return;
    }

    writeLdProductNowAnswers(answers);
    writePrevBrandAnswers({ brand: 'LD', variant });
    writePrevBrandAnswerAnswers({
      ...readPrevBrandAnswerAnswers(),
      smokedOtherProduct: answers.smokedOtherProduct,
    });

    setWarning(null);
    if (answers.smokedOtherProduct === 'yes') {
      navigate('/survey/prevbrand-other');
      return;
    }
    navigate('/survey/set-b/brand-opinion');
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              <SurveyFormPanel className="flex flex-col gap-2">
                <p id="ld-variant-question" className={surveyQuestionTextClass}>
                  {t.ldProductNow.variantQuestion}
                </p>
                <select
                  id="ld-variant"
                  value={answers.variant}
                  disabled={loading}
                  onChange={(e) => {
                    setAnswers((prev) => ({ ...prev, variant: e.target.value }));
                    setWarning(null);
                  }}
                  className={`${inputClass} mt-0`}
                  aria-labelledby="ld-variant-question"
                >
                  <option value="" disabled>
                    {loading ? t.ldProductNow.loading : t.ldProductNow.selectVariant}
                  </option>
                  {variants.map((variant) => (
                    <option key={variant} value={variant}>
                      {variant}
                    </option>
                  ))}
                </select>
              </SurveyFormPanel>

              <SurveyFormPanel className="flex flex-col gap-2">
                <p id="ld-motivation-question" className={surveyQuestionTextClass}>
                  {t.ldProductNow.motivationQuestion}
                </p>

                <div
                  role="group"
                  aria-labelledby="ld-motivation-question"
                  className="flex flex-col gap-1.5"
                >
                  {LD_MOTIVATION_OPTION_KEYS.map((key) => (
                    <label
                      key={key}
                      htmlFor={`ld-motivation-${key}`}
                      className="flex cursor-pointer items-start gap-1.5"
                    >
                      <input
                        id={`ld-motivation-${key}`}
                        type="checkbox"
                        checked={answers.motivations[key]}
                        onChange={() => toggleMotivation(key)}
                        className={surveyChoiceInputClass}
                      />
                      <span className={labelClass}>{t.ldProductNow[key]}</span>
                    </label>
                  ))}

                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="ld-motivation-other-checkbox"
                      className="flex cursor-pointer items-start gap-1.5"
                    >
                      <input
                        id="ld-motivation-other-checkbox"
                        type="checkbox"
                        checked={answers.motivations.other}
                        onChange={toggleOther}
                        className={surveyChoiceInputClass}
                      />
                      <span className={labelClass}>{t.ldProductNow.otherSpecify}</span>
                    </label>
                    <input
                      id="ld-motivation-other"
                      type="text"
                      value={answers.motivations.otherSpecify}
                      onChange={(e) => {
                        const otherSpecify = e.target.value;
                        setAnswers((prev) => ({
                          ...prev,
                          motivations: {
                            ...prev.motivations,
                            other: true,
                            otherSpecify,
                          },
                        }));
                        setWarning(null);
                      }}
                      onFocus={() => {
                        if (!answers.motivations.other) toggleOther();
                      }}
                      className={inputClass}
                    />
                  </div>
                </div>
              </SurveyFormPanel>

              <SurveyFormPanel className="flex flex-col gap-1.5">
                <p id="ld-smoked-other-question" className={surveyQuestionTextClass}>
                  {t.ldProductNow.otherProductQuestion}
                </p>
                <select
                  id="ld-smoked-other"
                  value={answers.smokedOtherProduct}
                  onChange={(e) => {
                    const smokedOtherProduct = e.target.value as LdProductNowAnswers['smokedOtherProduct'];
                    if (smokedOtherProduct === 'no') {
                      clearOtherPrevBrandAnswers();
                      clearOtherPrevBrandAnswerAnswers();
                    } else if (smokedOtherProduct === 'yes') {
                      clearSetBLdBrandOpinionAnswers();
                    }
                    setAnswers((prev) => ({ ...prev, smokedOtherProduct }));
                    setWarning(null);
                  }}
                  className={inputClass}
                  aria-labelledby="ld-smoked-other-question"
                >
                  <option value="" disabled>
                    {t.ldProductNow.selectYesNo}
                  </option>
                  <option value="yes">{t.ldProductNow.yes}</option>
                  <option value="no">{t.ldProductNow.no}</option>
                </select>
              </SurveyFormPanel>
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-2 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" disabled={loading} onClick={handleContinue}>
                {t.ldProductNow.continue}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
